import React, { useEffect, useRef } from 'react';
import {
  motion,
  MotionValue,
  useMotionValue,
  useSpring,
  useTransform,
} from 'framer-motion';

import { EASY_STEPS } from '../content';

const clampProgress = (value: number) => Math.min(1, Math.max(0, value));
const CENTER_LOCK_THRESHOLD = 28;
type ScrollDirection = -1 | 0 | 1;

interface EasyStepItemProps {
  label: string;
  index: number;
  progress: MotionValue<number>;
}

const EasyStepItem: React.FC<EasyStepItemProps> = ({
  label,
  index,
  progress,
}) => {
  const start = 0.08 + index * 0.28;
  const numberEnd = start + 0.1;
  const textStart = start + 0.08;
  const textEnd = start + 0.22;
  const numberOpacityRaw = useTransform(
    progress,
    [0, start, numberEnd, 1],
    [0.28, 0.28, 1, 1]
  );
  const textOpacityRaw = useTransform(
    progress,
    [0, textStart, textEnd, 1],
    [0, 0, 1, 1]
  );
  const textYRaw = useTransform(
    progress,
    [0, textStart, textEnd, 1],
    [92, 92, 0, 0]
  );
  const spring = { stiffness: 90, damping: 24, mass: 0.35 };
  const numberOpacity = useSpring(numberOpacityRaw, spring);
  const textOpacity = useSpring(textOpacityRaw, spring);
  const textY = useSpring(textYRaw, spring);

  return (
    <div className="relative flex h-[220px] items-center justify-center md:h-[300px]">
      <motion.span
        style={{ opacity: numberOpacity }}
        className="select-none text-[200px] font-black leading-none tracking-tighter md:text-[300px]"
      >
        {index + 1}
      </motion.span>
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.p
          style={{ opacity: textOpacity, y: textY }}
          className="w-[260px] text-center text-xl font-extrabold leading-snug tracking-tight text-[#23262C] lg:w-[340px] lg:text-2xl"
        >
          {label}
        </motion.p>
      </div>
    </div>
  );
};

export const EasySteps: React.FC = () => {
  const stepsRef = useRef<HTMLDivElement>(null);
  const progress = useMotionValue(0);
  const progressRef = useRef(0);
  const isLockedRef = useRef(false);
  const lockScrollYRef = useRef<number | null>(null);
  const lastDistanceToCenterRef = useRef<number | null>(null);
  const lastScrollYRef = useRef(0);
  const scrollFrameRef = useRef<number | null>(null);
  const lastTouchPointRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const setProgress = (value: number) => {
      const next = clampProgress(value);
      progressRef.current = next;
      progress.set(next);
    };

    const getDistanceToViewportCenter = () => {
      const steps = stepsRef.current;
      if (!steps) return null;

      const rect = steps.getBoundingClientRect();
      return rect.top + rect.height / 2 - window.innerHeight / 2;
    };

    const scrollToY = (targetScrollY: number) => {
      const lenis = window.__landingLenis;

      if (lenis) {
        lenis.scrollTo(targetScrollY, { immediate: true, force: true });
        return;
      }

      window.scrollTo({ top: targetScrollY, behavior: 'auto' });
    };

    const lockAtCurrentCenter = (distanceToCenter: number) => {
      const targetScrollY = window.scrollY + distanceToCenter;
      lockScrollYRef.current = targetScrollY;
      window.__landingScrollLocked = true;
      scrollToY(targetScrollY);
      isLockedRef.current = true;
      lastDistanceToCenterRef.current = 0;
      lastScrollYRef.current = targetScrollY;
    };

    const releaseLock = () => {
      isLockedRef.current = false;
      lockScrollYRef.current = null;
      window.__landingScrollLocked = false;
      lastDistanceToCenterRef.current = getDistanceToViewportCenter();
      lastScrollYRef.current = window.scrollY;
    };

    const consumeScrollEvent = (event: Event) => {
      if (event.cancelable) {
        event.preventDefault();
      }

      event.stopPropagation();
      event.stopImmediatePropagation();
    };

    const holdLockPosition = () => {
      const targetScrollY = lockScrollYRef.current;
      if (targetScrollY === null) return;
      if (Math.abs(window.scrollY - targetScrollY) <= 1) return;

      scrollToY(targetScrollY);
    };

    const getScrollDirection = (): ScrollDirection => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollYRef.current) return 1;
      if (currentScrollY < lastScrollYRef.current) return -1;
      return 0;
    };

    const shouldLockAtCenter = (
      distanceToCenter: number,
      direction: ScrollDirection
    ) => {
      if (direction === 0 || window.innerWidth < 768) return false;

      const currentProgress = progressRef.current;
      const canPlayForward = direction > 0 && currentProgress < 1;
      const canPlayBackward = direction < 0 && currentProgress > 0;
      if (!canPlayForward && !canPlayBackward) return false;

      const previousDistance = lastDistanceToCenterRef.current;
      const isNearCenter = Math.abs(distanceToCenter) <= CENTER_LOCK_THRESHOLD;
      if (previousDistance === null) return isNearCenter;

      const crossedCenter =
        direction > 0
          ? previousDistance > 0 && distanceToCenter <= 0
          : previousDistance < 0 && distanceToCenter >= 0;

      return isNearCenter || crossedCenter;
    };

    const updateScrollCheckpoint = () => {
      lastDistanceToCenterRef.current = getDistanceToViewportCenter();
      lastScrollYRef.current = window.scrollY;
    };

    const handleScroll = () => {
      if (scrollFrameRef.current !== null) return;

      scrollFrameRef.current = window.requestAnimationFrame(() => {
        scrollFrameRef.current = null;

        const distanceToCenter = getDistanceToViewportCenter();
        if (distanceToCenter === null) return;

        if (isLockedRef.current) {
          holdLockPosition();
          lastDistanceToCenterRef.current = 0;
          lastScrollYRef.current = lockScrollYRef.current ?? window.scrollY;
          return;
        }

        const direction = getScrollDirection();
        if (shouldLockAtCenter(distanceToCenter, direction)) {
          lockAtCurrentCenter(distanceToCenter);
          return;
        }

        lastDistanceToCenterRef.current = distanceToCenter;
        lastScrollYRef.current = window.scrollY;
      });
    };

    const handleScrollIntent = (deltaY: number, event: Event) => {
      if (window.innerWidth < 768 || deltaY === 0) return;

      const distanceToCenter = getDistanceToViewportCenter();
      if (distanceToCenter === null) return;

      const currentProgress = progressRef.current;
      const isScrollingDown = deltaY > 0;
      const isScrollingUp = deltaY < 0;
      const canPlayForward = isScrollingDown && currentProgress < 1;
      const canPlayBackward = isScrollingUp && currentProgress > 0;
      const canPlay = canPlayForward || canPlayBackward;
      const isTryingToExitForward = isScrollingDown && currentProgress >= 1;
      const isTryingToExitBackward = isScrollingUp && currentProgress <= 0;

      if (!isLockedRef.current) {
        if (!canPlay) return;

        const nextDistanceToCenter = distanceToCenter - deltaY;
        const wouldCrossCenter =
          Math.sign(distanceToCenter) !== Math.sign(nextDistanceToCenter);
        const isNearCenter =
          Math.abs(distanceToCenter) <= CENTER_LOCK_THRESHOLD;

        if (!wouldCrossCenter && !isNearCenter) return;

        consumeScrollEvent(event);
        lockAtCurrentCenter(distanceToCenter);
      }

      if (!canPlay && (isTryingToExitForward || isTryingToExitBackward)) {
        releaseLock();
        return;
      }

      consumeScrollEvent(event);
      holdLockPosition();

      const cappedDelta =
        Math.sign(deltaY) * Math.min(Math.abs(deltaY), 110);
      const nextProgress = clampProgress(currentProgress + cappedDelta / 950);
      setProgress(nextProgress);
    };

    const handleWheel = (event: WheelEvent) => {
      handleScrollIntent(event.deltaY, event);
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (window.innerWidth < 768 || event.touches.length !== 1) return;

      const touch = event.touches[0];
      lastTouchPointRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (window.innerWidth < 768 || event.touches.length !== 1) return;

      const previousTouch = lastTouchPointRef.current;
      if (!previousTouch) return;

      const touch = event.touches[0];
      const deltaX = previousTouch.x - touch.clientX;
      const deltaY = previousTouch.y - touch.clientY;

      lastTouchPointRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };

      if (Math.abs(deltaY) < 1 || Math.abs(deltaX) > Math.abs(deltaY)) return;

      handleScrollIntent(deltaY, event);
    };

    const handleTouchEnd = () => {
      lastTouchPointRef.current = null;
    };

    updateScrollCheckpoint();

    window.addEventListener('wheel', handleWheel, {
      capture: true,
      passive: false,
    });
    window.addEventListener('touchstart', handleTouchStart, {
      capture: true,
      passive: true,
    });
    window.addEventListener('touchmove', handleTouchMove, {
      capture: true,
      passive: false,
    });
    window.addEventListener('touchend', handleTouchEnd, { capture: true });
    window.addEventListener('touchcancel', handleTouchEnd, { capture: true });
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('wheel', handleWheel, { capture: true });
      window.removeEventListener('touchstart', handleTouchStart, {
        capture: true,
      });
      window.removeEventListener('touchmove', handleTouchMove, {
        capture: true,
      });
      window.removeEventListener('touchend', handleTouchEnd, {
        capture: true,
      });
      window.removeEventListener('touchcancel', handleTouchEnd, {
        capture: true,
      });
      window.removeEventListener('scroll', handleScroll);
      if (scrollFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
      window.__landingScrollLocked = false;
    };
  }, [progress]);

  return (
    <section className="relative hidden px-4 py-10 md:block md:px-8 md:py-14">
      <div>
        <div className="mx-auto w-full max-w-[1180px]">
          <h2 className="mx-auto max-w-[1100px] text-center font-['Inter'] text-[58px] font-light leading-[1.04] text-[#23262C]">
            3 Easy Steps to Get Covered
          </h2>

          <div
            ref={stepsRef}
            className="mt-10 grid gap-2 text-[#e7e1d6] md:grid-cols-3"
          >
            {EASY_STEPS.map((label, i) => (
              <EasyStepItem
                key={label}
                label={label}
                index={i}
                progress={progress}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
